/**
 * Bonus detail action buttons per status group, mirroring the bonus card switcher
 * (@atlas/components BonusCard renderActions):
 *   available -> Deposit (deposit-like) / Activate (everything else, incl. freespins)
 *   unlocked  -> Activate (Remove dropped after testing, FT-8148)
 *   activated -> Play + Cancel (freespin, Cancel CMS-gated) / Cancel (everything else; hidden when hideBonusCancel)
 *   finished  -> none (already on the detail page; the card shows a "details" button)
 *
 * Each entry is a plain descriptor { key, label, type?, onClick }. Handlers are
 * supplied by useBonusActions and looked up by name, so adding or changing a
 * handler never touches this map and the renderer stays a flat list.
 */
// Button keys (React key + handler name) and i18n label keys, extracted to avoid magic
// strings. Labels match the key except activate-and-play (separate translation).
const ACTION_KEY = {
  ACTIVATE: 'activate',
  ACTIVATE_AND_PLAY: 'activateAndPlay',
  CANCEL: 'cancel',
  DEPOSIT: 'deposit',
  PLAY: 'play',
  REMOVE: 'remove',
};

const ACTION_LABEL = {
  ACTIVATE: 'activate',
  ACTIVATE_AND_PLAY: 'activate_and_play',
  CANCEL: 'cancel',
  DEPOSIT: 'deposit',
  PLAY: 'play',
  REMOVE: 'remove',
};

const BUTTON_TYPE = { SECONDARY: 'secondary' };

const ACTION_GROUP_BUTTONS = {
  activated: ({ actions, hideCancel, isFreeSpin }) => {
    if (isFreeSpin) {
      // Activated freespins show Play + Cancel (Cancel is CMS-gated via hideCancel).
      const buttons = [{ key: ACTION_KEY.PLAY, label: ACTION_LABEL.PLAY, onClick: actions.play }];
      if (!hideCancel) {
        buttons.push({
          key: ACTION_KEY.CANCEL,
          label: ACTION_LABEL.CANCEL,
          onClick: actions.cancel,
          type: BUTTON_TYPE.SECONDARY,
        });
      }
      return buttons;
    }
    if (hideCancel) return [];
    return [
      {
        key: ACTION_KEY.CANCEL,
        label: ACTION_LABEL.CANCEL,
        onClick: actions.cancel,
        type: BUTTON_TYPE.SECONDARY,
      },
    ];
  },

  // Deposit / reload-deposit -> Deposit; everything else (incl. freespins) -> Activate.
  // Per design the combined "Activate & Play" lives in the eligible-games section, not here.
  available: ({ actions, isDepositLike }) =>
    isDepositLike
      ? [{ key: ACTION_KEY.DEPOSIT, label: ACTION_LABEL.DEPOSIT, onClick: actions.deposit }]
      : [{ key: ACTION_KEY.ACTIVATE, label: ACTION_LABEL.ACTIVATE, onClick: actions.activate }],

  // Unlocked shows Activate only — Remove was dropped after testing (FT-8148),
  // for both freespins and regular bonuses.
  unlocked: ({ actions }) => [
    { key: ACTION_KEY.ACTIVATE, label: ACTION_LABEL.ACTIVATE, onClick: actions.activate },
  ],
};

export const getBonusActionButtons = ({
  actionGroup,
  actions,
  hideCancel,
  isDepositLike,
  isFreeSpin,
}) => {
  const build = ACTION_GROUP_BUTTONS[actionGroup];
  return build ? build({ actions, hideCancel, isDepositLike, isFreeSpin }) : [];
};
